
import React, { useEffect, useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { Row, Col } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router";
import Breadcrumbdasbord from "../../component/Breadcrumbdasbord";
import Sidebarmenu from "../../component/SidebarNavigation";
import "../../style/Dragondrop.css";
import { getCustomers } from "../../../Cart/service/Customers.service";
import { getBulkupload } from "../../service/bulkupload.service";
import { getCartCount } from "../../../../Component/header/service/CartCount.service";
import BulkOrders from "../../../../assets/ExcelFile/BulkOrders.xlsx";

function Dragondrop() {
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showCustomers, setShowCustomers] = useState(false);
  const [activeCustomerIndex, setActiveCustomerIndex] = useState(-1);
  const [cartCount, setCartCount] = useState(0);
  const [checkingCart, setCheckingCart] = useState(true);
  const [uploading, setUploading] = useState(false);
  // 0 = hidden, 1 -> 2 -> 3
  const [redirectTimer, setRedirectTimer] = useState(0);
  const redirectIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  // -----------------------------
  // HIGHLIGHT CUSTOMER SEARCH TEXT
  // -----------------------------
  const highlightText = (text = "", searchValue = "") => {
    const value = String(text);
    const searchText = String(searchValue).trim();
    if (!searchText) {
      return value;
    }
    const escaped = searchText.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    const regex = new RegExp(`(${escaped})`, "gi");
    return value.split(regex).map((part, index) => {
      if (
        part.toLowerCase() === searchText.toLowerCase()
      ) {
        return (
          <span
            key={index}
            style={{
              fontWeight: 700,
              color: "#000",
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // -----------------------------
  // CART COUNT
  // -----------------------------
  const checkCartCount = async (
    selectedCustomer = customer
  ) => {
    try {
      setCheckingCart(true);
      const customerId =
        selectedCustomer?.id ??
        selectedCustomer?.customer_id ??
        selectedCustomer?.customerId ??
        null;

      const response = await getCartCount(
        "fresh",
        1,
        customerId
      );

      const count = Number(
        response?.data?.count ??
          response?.data?.cart_count ??
          response?.count ??
          response?.cart_count ??
          0
      );

      setCartCount(count);

      return count;
    } catch (error) {
      console.error("Cart Count Error:", error);
      setCartCount(0);
      return 0;
    } finally {
      setCheckingCart(false);
    }
  };

  useEffect(() => {
    checkCartCount();
    return () => {
      if (redirectIntervalRef.current) {
        clearInterval(redirectIntervalRef.current);
      }
    };
  });

  // -----------------------------
  // CUSTOMER SEARCH API
  // -----------------------------
  useEffect(() => {
    const search = customerSearch.trim();

    if (!search || customer) {
      setCustomers([]);
      setShowCustomers(false);
      setActiveCustomerIndex(-1);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await getCustomers(search);

        const data =
          response?.data?.data ??
          response?.data ??
          response ??
          [];

        const customerList = Array.isArray(data)
          ? data
          : [];

        setCustomers(customerList);

        if (customerList.length > 0) {
          setShowCustomers(true);
          setActiveCustomerIndex(0);
        } else {
          setShowCustomers(false);
          setActiveCustomerIndex(-1);
        }
      } catch (error) {
        console.error(
          "Customer Search Error:",
          error
        );

        setCustomers([]);
        setShowCustomers(false);
        setActiveCustomerIndex(-1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, customer]);

  // -----------------------------
  // CUSTOMER SELECT
  // -----------------------------
  const handleCustomerSelect = async (
    selectedCustomer
  ) => {
    if (!selectedCustomer) return;

    setCustomer(selectedCustomer);

    const customerName =
      selectedCustomer?.accountName ||
      selectedCustomer?.name ||
      selectedCustomer?.customer_name ||
      "";

    setCustomerSearch(customerName);

    // IMPORTANT:
    // Dropdown immediately close after selection
    setShowCustomers(false);
    setActiveCustomerIndex(-1);

    await checkCartCount(selectedCustomer);
  };

  // -----------------------------
  // CUSTOMER INPUT KEYBOARD
  // -----------------------------
  const handleCustomerKeyDown = (e) => {
    // If dropdown is not visible
    if (
      !showCustomers ||
      customers.length === 0
    ) {
      return;
    }

    // Arrow Down
    if (e.key === "ArrowDown") {
      e.preventDefault();

      setActiveCustomerIndex((prev) => {
        if (prev < customers.length - 1) {
          return prev + 1;
        }

        return 0;
      });

      return;
    }

    // Arrow Up
    if (e.key === "ArrowUp") {
      e.preventDefault();

      setActiveCustomerIndex((prev) => {
        if (prev > 0) {
          return prev - 1;
        }

        return customers.length - 1;
      });

      return;
    }

    // Enter
    if (e.key === "Enter") {
      e.preventDefault();

      if (activeCustomerIndex >= 0) {
        handleCustomerSelect(
          customers[activeCustomerIndex]
        );
      }

      return;
    }

    // Escape
    if (e.key === "Escape") {
      e.preventDefault();

      setShowCustomers(false);
      setActiveCustomerIndex(-1);
    }
  };

  // -----------------------------
  // FILE
  // -----------------------------
  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const allowedExtensions = /\.(xls|xlsx)$/i;

    const isValidFile =
      allowedTypes.includes(selectedFile.type) ||
      allowedExtensions.test(selectedFile.name);

    if (!isValidFile) {
      toast.error(
        "Please select XLS or XLSX file."
      );
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error(
        "File size should be less than 10MB."
      );
      return;
    }

    setFile(selectedFile);
  };

  const handleBrowse = () => {
    if (!customer) {
      toast.error(
        "Please select customer first."
      );
      return;
    }

    if (cartCount > 0) {
      toast.error(
        "Please remove cart data first."
      );
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    handleFile(e.target.files?.[0]);
  };

  // -----------------------------
  // DRAG DROP
  // -----------------------------
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!customer || cartCount > 0) return;

    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);

    if (!customer) {
      toast.error(
        "Please select customer first."
      );
      return;
    }

    if (cartCount > 0) {
      toast.error(
        "Please remove cart data first."
      );
      return;
    }

    handleFile(e.dataTransfer.files?.[0]);
  };

  // -----------------------------
  // REMOVE FILE
  // -----------------------------
  const handleRemove = (e) => {
    if (e) {
      e.stopPropagation();
    }

    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // -----------------------------
  // REDIRECT COUNTER
  // -----------------------------
  const startCartRedirect = () => {
    if (redirectIntervalRef.current) {
      clearInterval(redirectIntervalRef.current);
    }

    setRedirectTimer(1);

    redirectIntervalRef.current = setInterval(() => {
      setRedirectTimer((prev) => {
        if (prev >= 3) {
          clearInterval(
            redirectIntervalRef.current
          );

          redirectIntervalRef.current = null;
          navigate("/cart?comingsoon=0");

          return 3;
        }

        return prev + 1;
      });
    }, 1000);
  };

  // -----------------------------
  // UPLOAD
  // -----------------------------
  const handleUpload = async () => {
    if (!customer) {
      toast.error(
        "Please select customer first."
      );
      return;
    }

    if (!file) {
      toast.error(
        "Please select a file first."
      );
      return;
    }

    const latestCartCount =
      await checkCartCount(customer);

    if (latestCartCount > 0) {
      toast.error(
        "Please remove cart data first."
      );
      return;
    }

    const customerId =
      customer?.id ??
      customer?.customer_id ??
      customer?.customerId;

    if (!customerId) {
      toast.error(
        "Customer ID not found."
      );
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();

      formData.append("file", file);
      formData.append(
        "customer_id",
        String(customerId)
      );
      formData.append("user_id", "1");

      const response =
        await getBulkupload(formData);

      const uploadData = response?.data;

      if (
        response?.success === true &&
        uploadData?.status === "COMPLETED"
      ) {
        const totalRows = Number(
          uploadData?.total_rows || 0
        );

        const successRows = Number(
          uploadData?.success_rows || 0
        );

        const failedRows = Number(
          uploadData?.failed_rows || 0
        );

        if (failedRows > 0) {
          toast.error(
            `Upload completed: ${successRows} successful, ${failedRows} failed.`
          );
        } else {
          toast.success(
            `Bulk upload completed successfully. ${successRows}/${totalRows} rows uploaded.`
          );
        }

        handleRemove();
        startCartRedirect();
        await checkCartCount(customer);

        return;
      }

      toast.error(
        response?.message ||
          "Bulk upload could not be completed."
      );
    } catch (error) {
      console.error(
        "Bulk Upload Error:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Bulk upload failed."
      );
    } finally {
      setUploading(false);
    }
  };

  // -----------------------------
  // DOWNLOAD TEMPLATE
  // -----------------------------
  const handleDownloadTemplate = () => {
    const link =
      document.createElement("a");

    link.href = BulkOrders;
    link.download = "BulkOrders.xlsx";

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  };

  const uploadDisabled =
    !customer ||
    !file ||
    cartCount > 0 ||
    checkingCart ||
    uploading ||
    redirectTimer > 0;

  return (
    <section className="profile-section">
      <div className="container-fluid">
        <Row>
            <Sidebarmenu />
          <Col lg={10} md={9}>
            <Breadcrumbdasbord
              title="My Orders"
              items={[
                {
                  label: "My Orders",
                  path: "/account/orders",
                },
                {
                  label: "Bulk Order",
                },
              ]}
            />

            <div className="customer-wrapper">
              {/* HEADER */}
              <div
                className="customer-header"
                style={{
                  justifyContent:
                    "space-between",
                }}
              >
                <strong>
                  Bulk Upload
                </strong>

                <button
                  type="button"
                  className="btn btn-primary"
                  title="Download Template"
                  onClick={
                    handleDownloadTemplate
                  }
                >
                  <i className="bi bi-download"></i>{" "}
                  Template
                </button>
              </div>

              {/* CUSTOMER */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginLeft: "5%",
                  marginTop: "10px",
                  flexWrap: "wrap",
                }}
              >
            
                <div
                  style={{
                    position: "relative",
                    width: "260px",
                  }}
                >
                  {/* CUSTOMER INPUT */}
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search Customer"
                    value={customerSearch}
                    onFocus={() => {
                      // Selected customer:
                      // dropdown will NOT open
                      if (customer) {
                        setShowCustomers(false);
                        setActiveCustomerIndex(-1);
                        return;
                      }

                      if (
                        customerSearch.trim() &&
                        customers.length > 0
                      ) {
                        setShowCustomers(true);

                        if (
                          activeCustomerIndex < 0
                        ) {
                          setActiveCustomerIndex(0);
                        }
                      }
                    }}
                    onChange={(e) => {
                      const value =
                        e.target.value;

                      setCustomerSearch(value);

                      // Typing means new customer search
                      setCustomer(null);
                      setCartCount(0);

                      setActiveCustomerIndex(0);

                      // Dropdown show only when typing
                      setShowCustomers(
                        Boolean(value.trim())
                      );
                    }}
                    onKeyDown={
                      handleCustomerKeyDown
                    }
                    style={{
                      width: "260px",
                    }}
                  />

                  {/* CUSTOMER DROPDOWN */}
                  {showCustomers &&
                    !customer &&
                    customerSearch.trim() &&
                    customers.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          width: "260px",
                          background: "#fff",
                          border:
                            "1px solid #ddd",
                          borderRadius: "4px",
                          zIndex: 1000,
                          maxHeight: "220px",
                          overflowY: "auto",
                          boxShadow:
                            "0 4px 10px rgba(0,0,0,0.08)",
                        }}
                      >
                        {customers.map(
                          (item, index) => {
                            const customerName =
                              item?.accountName ||
                              item?.name ||
                              item?.customer_name ||
                              "Customer";

                            const customerId =
                              item?.id ??
                              item?.customer_id ??
                              item?.customerId ??
                              index;

                            const isActive =
                              activeCustomerIndex ===
                              index;

                            return (
                              <div
                                key={customerId}
                                onMouseDown={(e) => {
                                  e.preventDefault();

                                  handleCustomerSelect(
                                    item
                                  );
                                }}
                                onMouseEnter={() => {
                                  setActiveCustomerIndex(
                                    index
                                  );
                                }}
                                style={{
                                  padding:
                                    "8px 10px",
                                  cursor:
                                    "pointer",
                                  borderBottom:
                                    "1px solid #eee",
                                  fontSize:
                                    "14px",
                                  background:
                                    isActive
                                      ? "#f8f9fa"
                                      : "#fff",
                                  transition:
                                    "background 0.15s ease",
                                }}
                              >
                                {highlightText(
                                  customerName,
                                  customerSearch
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                </div>

                {/* CART WARNING */}
                {cartCount > 0 && (
                  <div
                      className="alert alert-warning d-inline-flex align-items-center mb-0"
                      role="alert"
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        marginLeft: "50px",
                        padding: "6px 12px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      Please remove cart data before continuing.
                    </div>
                )}

                {/* CUSTOMER SELECTED */}
                {customer &&
                  cartCount === 0 && (
                    <span
                      style={{
                        color: "#198754",
                        fontSize: "13px",
                        fontWeight: 600,
                        marginLeft: "50px",
                      }}
                    >
                      Customer selected
                    </span>
                  )}
              </div>

              {/* UPLOAD BOX */}
              <div
                className="drag-upload-wrapper"
                style={{
                  opacity:
                    !customer ||
                    cartCount > 0
                      ? 0.6
                      : 1,
                }}
              >
                <div
                  className={`drag-upload-box ${
                    isDragging
                      ? "drag-upload-active"
                      : ""
                  }`}
                  onClick={handleBrowse}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    cursor:
                      !customer ||
                      cartCount > 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={handleFileChange}
                    hidden
                    disabled={
                      !customer ||
                      cartCount > 0
                    }
                  />

                  {file ? (
                    <div className="drag-preview">
                      <div className="excel-file-preview">
                        <i className="bi bi-file-earmark-excel-fill"></i>

                        <div className="excel-file-info">
                          <strong>
                            {file.name}
                          </strong>

                          <small>
                            {(
                              file.size /
                              1024 /
                              1024
                            ).toFixed(2)}{" "}
                            MB
                          </small>

                          <span>
                            Excel File
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="drag-remove-btn"
                        onClick={
                          handleRemove
                        }
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="drag-upload-icon">
                        <Upload size={28} />
                      </div>

                      <h5>
                        {!customer
                          ? "Please Select Customer"
                          : cartCount > 0
                          ? "Please Empty Cart First"
                          : "Drag & Drop File"}
                      </h5>

                      <p>
                        {!customer
                          ? "Select customer to upload file"
                          : cartCount > 0
                          ? "Remove cart data before upload"
                          : (
                            <>
                              Drag your file here or{" "}
                              <span>
                                click to attach
                              </span>
                            </>
                          )}
                      </p>

                      <small>
                        XLS, XLSX up to 10MB
                      </small>
                    </>
                  )}
                </div>

                {/* UPLOAD BUTTON + COUNTER */}
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "flex-end",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  {redirectTimer > 0 && (
                    <span
                      style={{
                        color: "#09a344",
                        fontSize: "14px",
                        fontWeight: 600,
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      Redirecting to Cart in{" "}
                      {redirectTimer}
                    </span>
                  )}

                  <button
                    type="button"
                    className="download-template-btn mt-2"
                    onClick={handleUpload}
                    disabled={
                      uploadDisabled
                    }
                    style={{
                      width: "121px",
                      padding: "10px",
                      opacity:
                        uploadDisabled
                          ? 0.6
                          : 1,
                      cursor:
                        uploadDisabled
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {uploading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>

                        <span>
                          UPLOADING...
                        </span>
                      </>
                    ) : (
                      <>
                        <Upload size={17} />
                        <span>
                          UPLOAD
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
}

export default Dragondrop;
